/**
 * Touches de révision (spécification §5), réglage du compte. Elles sont enregistrées par
 * emplacement physique (`KeyboardEvent.code`), indépendant de la disposition du clavier :
 * les quatre touches à droite de la rangée du milieu. Les boutons affichent le caractère
 * marqué sur le clavier de l'utilisateur (à obtenir côté interface, par exemple avec
 * `navigator.keyboard.getLayoutMap()`).
 */

export type ActionClavier = 'a_revoir' | 'difficile' | 'bien' | 'facile' | 'modifier' | 'annuler';

export type Touches = Record<ActionClavier, string | null>;

/** Espace retourne la carte : elle ne peut pas servir à autre chose. */
export const TOUCHE_RETOURNER = 'Space';

export const TOUCHES_PAR_DEFAUT: Touches = {
  a_revoir: 'KeyL', // L en AZERTY Mac comme en QWERTY
  difficile: 'Semicolon', // M en AZERTY Mac, ; en QWERTY
  bien: 'Quote', // ù en AZERTY Mac, ' en QWERTY
  facile: 'Backslash', // ` en AZERTY Mac, \ en QWERTY
  modifier: 'KeyR',
  annuler: null, // raccourci à définir (spécification §13)
};

/** Note associée aux actions de notation. */
export const NOTE_DE_L_ACTION = { a_revoir: 1, difficile: 2, bien: 3, facile: 4 } as const;

/** Action déclenchée par une touche physique, ou `null`. */
export function actionDeLaTouche(touches: Touches, code: string): ActionClavier | null {
  for (const [action, touche] of Object.entries(touches) as [ActionClavier, string | null][]) {
    if (touche === code) return action;
  }
  return null;
}

/** Vérifie les touches. Renvoie les erreurs (vide si valides). */
export function validerTouches(touches: Touches): string[] {
  const erreurs: string[] = [];
  const utilisees = Object.values(touches).filter((code): code is string => code !== null);
  if (utilisees.includes(TOUCHE_RETOURNER)) erreurs.push('Espace sert à retourner la carte.');
  if (new Set(utilisees).size !== utilisees.length) {
    erreurs.push('Une même touche est attribuée à deux actions.');
  }
  for (const action of ['a_revoir', 'difficile', 'bien', 'facile'] as const) {
    if (touches[action] === null) erreurs.push('Chaque note doit avoir une touche.');
  }
  return [...new Set(erreurs)];
}
